import { Formik, Form } from 'formik';
import React, { useMemo } from 'react';
import usersValidationSchema from './validationSchema';
import Input from 'component/atom/forms/input/input/Input';
import Select from 'component/atom/forms/select/select/Select';
import styles from './GroupForm.module.scss';
import { useFetch } from 'hooks/useFetch';
import { groupEndpoints, scriptEndpoints } from 'hooks/endpoints';
import Loader from 'component/atom/loader/Loader';
import TextArea from 'component/atom/forms/text-area/TextArea';
import MultiSelectList from 'component/atom/forms/select/multi-select-list/MultiSelectList';
import usersIcon from '../../../../../../../assets/icons/vector/users.svg';
import adminIcon from '../../../../../../../assets/icons/vector/admin.svg';

const GroupForm = ({
  id,
  data,
  isFetching,
  formRef,
  AddGroup,
  EditGroup,
  onClose,
  setRefetch,
  refetch,
  setSelectedGroup,
}) => {
  const { data: bots, isFetching: isFetchingBots } = useFetch(
    ['allBots'],
    scriptEndpoints.getBots(),
  );

  const fetchedData = data;

  const normalizeBotIds = (val) => {
    if (Array.isArray(val)) return val.filter(Boolean);
    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (!trimmed) return [];
      return trimmed
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
    }
    if (val == null) return [];
    return [val].filter(Boolean);
  };

  const initialvalues = {
    id: id || '00000000-0000-0000-0000-000000000000',
    groupName: fetchedData?.groupName || '',
    description: fetchedData?.description || '',
    roleId: fetchedData?.roleId || '',
    botIds: normalizeBotIds(fetchedData?.botIds ?? fetchedData?.botId),
  };

  const botOptions = useMemo(
    () =>
      bots?.data?.map((val) => ({
        value: val.botId,
        label: val.botName,
      })),
    [bots],
  );

  const handleSubmitForm = (values) => {
    const botIdsArr = Array.isArray(values.botIds) ? values.botIds : [];

    const payload = {
      id: values.id,
      groupName: values.groupName,
      description: values.description,
      roleId: values.roleId,
      botIds: botIdsArr,
    };
    id
      ? EditGroup(
          {
            url: groupEndpoints?.updateUserGroup(),
            data: payload,
            method: 'patch',
          },
          {
            onSuccess: (data) => {
              setRefetch(!refetch);
              setSelectedGroup(null);
              onClose();
            },
            onError: () => {},
          },
        )
      : AddGroup(
          {
            url: groupEndpoints?.createUserGroup(),
            data: payload,
          },
          {
            onSuccess: () => {
              setRefetch(!refetch);
              setSelectedGroup(null);
              onClose();
            },
            onError: () => {},
          },
        );
  };

  if (isFetching) {
    return <Loader isComponent />;
  }

  return (
    <Formik
      initialValues={initialvalues}
      validationSchema={usersValidationSchema(id)}
      enableReinitialize={true}
      onSubmit={(values) => {
        handleSubmitForm(values);
      }}
      innerRef={formRef}
    >
      {({
        values,
        errors,
        touched,
        handleBlur,
        handleChange,
        setFieldValue,
      }) => {
        return (
          <Form className={styles.formCont}>
            <div className={styles.cont}>
              <div className={styles.subCont}>
                <Input
                  label='Group Name'
                  placeholder='Enter Name'
                  name='groupName'
                  value={values.groupName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.groupName}
                  touched={touched.groupName}
                />

                <TextArea
                  label='Description'
                  value={values.description}
                  name='description'
                  onChange={handleChange}
                  error={errors.description}
                  touched={touched.description}
                  placeholder='Enter Description'
                />
                <div className={styles['roles-container']}>
                  <p>Role and Permissions</p>
                  <div className={styles['roles-cards']}>
                    <div
                      className={`${styles['card']} ${2 === values.roleId && styles['selected']}`}
                      onClick={() => setFieldValue('roleId', 2)}
                    >
                      <div className={styles['user-icon']}>
                        <img src={usersIcon} />
                      </div>
                      <div className={styles['title-container']}>
                        <p className={styles['title']}>User</p>
                        <p className={styles['desc']}>
                          Basic access to view content
                        </p>
                      </div>
                    </div>
                    <div
                      className={`${styles['card']} ${1 === values.roleId && styles['selected']}`}
                      onClick={() => setFieldValue('roleId', 1)}
                    >
                      <div className={styles['user-icon']}>
                        <img src={adminIcon} />
                      </div>
                      <div className={styles['title-container']}>
                        <p className={styles['title']}>Admin</p>
                        <p className={styles['desc']}>
                          Full administrative privileges
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                {2 === values?.roleId && (
                  <MultiSelectList
                    label='Chatbot'
                    name='botIds'
                    value={values.botIds}
                    onChange={handleChange}
                    options={botOptions}
                    placeholder='Select Bots'
                    error={errors.botIds}
                    touched={touched.botIds}
                  />
                )}
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default GroupForm;
